import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Save, X, Search, ChevronLeft, ChevronRight, ImagePlus, Factory } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ItemMaster = () => {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [plants, setPlants] = useState([]);
  const [editDialog, setEditDialog] = useState({ open: false, item: null });
  const [editFormData, setEditFormData] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const [createImageFile, setCreateImageFile] = useState(null);
  const [createImagePreview, setCreateImagePreview] = useState(null);
  const [imagePreview, setImagePreview] = useState({ open: false, src: '', alt: '' });
  const [plantAssignDialog, setPlantAssignDialog] = useState({ open: false, item: null, selectedPlants: [] });
  const [newItem, setNewItem] = useState({
    part_number: '',
    description: '',
    category: 'RM-Bar',
    uom: 'EA',
    cycle_time_hrs: 0,
    safety_stock_days: 0,
    customer_id: '',
    plant_ids: []
  });
  
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [plantFilter, setPlantFilter] = useState('all');
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    fetchItems();
    fetchCustomers();
    fetchPlants();
  }, []);

  const fetchItems = async () => {
    try {
      // Let the Axios interceptor pass the active plant_id from the header switcher
      const response = await axios.get(`${API}/items`);
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchPlants = async () => {
    try {
      const response = await axios.get(`${API}/plants`);
      setPlants(response.data.filter(p => p.is_active));
    } catch (error) {
      console.error('Error fetching plants:', error);
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.name : '-';
  };

  const getPlantName = (plantId) => {
    const plant = plants.find(p => p.id === plantId);
    return plant ? `${plant.code} - ${plant.name}` : plantId;
  };

  const filteredItems = useMemo(() => {
    let result = items;
    
    if (categoryFilter && categoryFilter !== 'all') {
      result = result.filter(item => item.category === categoryFilter);
    }
    
    if (customerFilter && customerFilter !== 'all') {
      result = result.filter(item => item.customer_id === customerFilter);
    }

    if (plantFilter && plantFilter !== 'all') {
      if (plantFilter === 'unassigned') {
        result = result.filter(item => !item.plant_ids || item.plant_ids.length === 0);
      } else {
        result = result.filter(item => item.plant_ids && item.plant_ids.includes(plantFilter));
      }
    }
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(item =>
        item.part_number?.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query)
      );
    }
    
    return result;
  }, [items, searchQuery, categoryFilter, customerFilter, plantFilter]);

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredItems.slice(startIndex, startIndex + pageSize);
  }, [filteredItems, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredItems.length / pageSize);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, categoryFilter, customerFilter, plantFilter]);

  const handleCreate = async () => {
    if (!newItem.part_number || !newItem.description) {
      toast.error('Part number and description are required');
      return;
    }

    if (newItem.category === 'FG' && !newItem.customer_id) {
      toast.error('Customer is required for Finished Goods (FG) items');
      return;
    }

    if (!newItem.plant_ids || newItem.plant_ids.length === 0) {
      toast.error('Please assign the item to at least one plant');
      return;
    }

    const normalizedPartNumber = newItem.part_number.trim();
    const existingItem = items.find(item => 
      item.part_number.toLowerCase() === normalizedPartNumber.toLowerCase()
    );
    
    if (existingItem) {
      toast.error(`Part number '${normalizedPartNumber}' already exists.`);
      return;
    }

    try {
      const itemToCreate = { 
        ...newItem, 
        part_number: normalizedPartNumber,
        customer_id: newItem.category === 'FG' ? newItem.customer_id : null
      };
      const response = await axios.post(`${API}/items`, itemToCreate);
      const createdItemId = response.data?.id;
      
      if (createImageFile && createdItemId) {
        try {
          const formData = new FormData();
          formData.append('file', createImageFile);
          await axios.post(`${API}/items/${createdItemId}/upload-image`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch (imgError) {
          toast.warning('Item created but image upload failed');
        }
      }
      
      toast.success('Item created successfully');
      setNewItem({ part_number: '', description: '', category: 'RM-Bar', uom: 'EA', cycle_time_hrs: 0, safety_stock_days: 0, customer_id: '', plant_ids: [] });
      setCreateImageFile(null);
      setCreateImagePreview(null);
      await fetchItems();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to create item';
      toast.error(errorMsg);
      fetchItems();
    }
  };

  const handleEditClick = (item) => {
    setEditDialog({ open: true, item });
    setEditFormData({
      part_number: item.part_number,
      description: item.description,
      category: item.category,
      uom: item.uom,
      cycle_time_hrs: item.cycle_time_hrs || 0,
      safety_stock_days: item.safety_stock_days || 0,
      customer_id: item.customer_id || ''
    });
  };

  const handleEditSave = async () => {
    if (!editFormData.part_number || !editFormData.description) {
      toast.error('Part number and description are required');
      return;
    }

    if (editFormData.category === 'FG' && !editFormData.customer_id) {
      toast.error('Customer is required for Finished Goods (FG) items');
      return;
    }

    try {
      const itemToUpdate = {
        ...editFormData,
        customer_id: editFormData.category === 'FG' ? editFormData.customer_id : null
      };
      await axios.put(`${API}/items/${editDialog.item.id}`, itemToUpdate);
      toast.success('Item updated successfully');
      setEditDialog({ open: false, item: null });
      fetchItems();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to update item';
      toast.error(errorMsg);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editDialog.item) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/items/${editDialog.item.id}/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Image uploaded successfully');
      setEditDialog(prev => ({
        ...prev,
        item: { ...prev.item, image_url: res.data.image_url }
      }));
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleImageDelete = async () => {
    if (!editDialog.item) return;
    try {
      await axios.delete(`${API}/items/${editDialog.item.id}/image`);
      toast.success('Image removed');
      setEditDialog(prev => ({
        ...prev,
        item: { ...prev.item, image_url: null }
      }));
      fetchItems();
    } catch (error) {
      toast.error('Failed to remove image');
    }
  };

  const openPlantAssignDialog = (item) => {
    setPlantAssignDialog({
      open: true,
      item,
      selectedPlants: [...(item.plant_ids || [])]
    });
  };

  const handlePlantToggle = (plantId) => {
    setPlantAssignDialog(prev => {
      const selected = prev.selectedPlants.includes(plantId)
        ? prev.selectedPlants.filter(id => id !== plantId)
        : [...prev.selectedPlants, plantId];
      return { ...prev, selectedPlants: selected };
    });
  };

  const handleSavePlantAssignment = async () => {
    try {
      await axios.post(`${API}/items/${plantAssignDialog.item.id}/assign-plants`, {
        plant_ids: plantAssignDialog.selectedPlants
      });
      toast.success(`Item assigned to ${plantAssignDialog.selectedPlants.length} plant(s)`);
      setPlantAssignDialog({ open: false, item: null, selectedPlants: [] });
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign plants');
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'FG': return 'bg-green-100 text-green-800';
      case 'SFG': return 'bg-orange-100 text-orange-800';
      case 'BOP': return 'bg-purple-100 text-purple-800';
      case 'RM-Sheet': return 'bg-blue-100 text-blue-800';
      case 'RM-Tube': return 'bg-cyan-100 text-cyan-800';
      case 'RM-Bar': return 'bg-indigo-100 text-indigo-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryDescription = (category) => {
    switch (category) {
      case 'FG': return 'Finished Goods';
      case 'SFG': return 'Semi-Finished (Make/Buy)';
      case 'BOP': return 'Bought-Out Parts (Buy Only)';
      case 'RM-Sheet': return 'Raw Material - Sheet';
      case 'RM-Tube': return 'Raw Material - Tube';
      case 'RM-Bar': return 'Raw Material - Bar';
      default: return category;
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete item "${item.part_number} - ${item.description}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/items/${item.id}`);
      toast.success('Item deleted successfully');
      fetchItems();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete item';
      toast.error(errorMessage, {
        duration: 6000,
        style: { maxWidth: '600px' }
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="font-medium text-blue-900 mb-2">Item Category Guidelines</p>
        <div className="text-sm text-blue-700 space-y-1">
          <p>- <strong>FG (Finished Goods):</strong> Final products sold to customers</p>
          <p>- <strong>SFG (Semi-Finished):</strong> Can be manufactured in-house OR purchased from vendors</p>
          <p>- <strong>BOP (Bought-Out Parts):</strong> MUST be purchased from vendors only</p>
          <p>- <strong>RM-Sheet/Tube/Bar:</strong> Raw materials purchased from vendors</p>
        </div>
      </div>

      <Card data-testid="item-create-card">
        <CardHeader>
          <CardTitle>Add New Item</CardTitle>
          <CardDescription>Create a new item with appropriate category and plant assignment</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Part Number *</label>
              <Input
                data-testid="input-part-number"
                placeholder="BRK-001"
                value={newItem.part_number}
                onChange={(e) => setNewItem({ ...newItem, part_number: e.target.value })}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Description *</label>
              <Input
                data-testid="input-description"
                placeholder="Brake Assembly"
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Category</label>
              <Select
                data-testid="select-category"
                value={newItem.category}
                onValueChange={(value) => setNewItem({ ...newItem, category: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FG">FG - Finished Goods</SelectItem>
                  <SelectItem value="SFG">SFG - Semi-Finished (Make/Buy)</SelectItem>
                  <SelectItem value="BOP">BOP - Bought-Out Parts (Buy Only)</SelectItem>
                  <SelectItem value="RM-Sheet">RM-Sheet - Raw Material Sheet</SelectItem>
                  <SelectItem value="RM-Tube">RM-Tube - Raw Material Tube</SelectItem>
                  <SelectItem value="RM-Bar">RM-Bar - Raw Material Bar</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">UOM</label>
              <Input
                data-testid="input-uom"
                placeholder="EA/KG"
                value={newItem.uom}
                onChange={(e) => setNewItem({ ...newItem, uom: e.target.value })}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Cycle Time (hrs)</label>
              <Input
                data-testid="input-cycle-time"
                type="number"
                step="0.1"
                value={newItem.cycle_time_hrs}
                onChange={(e) => setNewItem({ ...newItem, cycle_time_hrs: parseFloat(e.target.value) || 0 })}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Safety Stock Days</label>
              <Input
                data-testid="input-safety-stock-days"
                type="number"
                min="0"
                step="1"
                placeholder="7"
                value={newItem.safety_stock_days}
                onChange={(e) => setNewItem({ ...newItem, safety_stock_days: parseInt(e.target.value) || 0 })}
              />
              <p className="text-xs text-gray-500 mt-1">Days of inventory to maintain</p>
            </div>

            {newItem.category === 'FG' && (
              <div>
                <label className="text-sm font-medium mb-2 block">Customer *</label>
                <Select
                  data-testid="select-customer"
                  value={newItem.customer_id}
                  onValueChange={(value) => setNewItem({ ...newItem, customer_id: value })}
                >
                  <SelectTrigger data-testid="select-customer-trigger">
                    <SelectValue placeholder="Select Customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-orange-600 mt-1">Required: FG items must be assigned to a customer</p>
              </div>
            )}

            {/* Plant Assignment for Create */}
            <div>
              <label className="text-sm font-medium mb-2 block">Assign to Plants *</label>
              <div className="border rounded-md p-3 space-y-2 max-h-32 overflow-y-auto bg-white">
                {plants.map(plant => (
                  <label key={plant.id} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-1 rounded">
                    <Checkbox
                      data-testid={`create-plant-checkbox-${plant.id}`}
                      checked={newItem.plant_ids.includes(plant.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setNewItem(prev => ({ ...prev, plant_ids: [...prev.plant_ids, plant.id] }));
                        } else {
                          setNewItem(prev => ({ ...prev, plant_ids: prev.plant_ids.filter(id => id !== plant.id) }));
                        }
                      }}
                    />
                    <span className="text-sm">{plant.code} - {plant.name}</span>
                  </label>
                ))}
              </div>
              {newItem.plant_ids.length === 0 && (
                <p className="text-xs text-orange-600 mt-1">Select at least one plant</p>
              )}
            </div>

            {/* Image Upload for Create */}
            <div>
              <label className="text-sm font-medium mb-2 block">Part Image</label>
              <div className="flex items-center gap-3">
                {createImagePreview ? (
                  <div className="relative group">
                    <img src={createImagePreview} alt="Preview" className="w-10 h-10 rounded object-cover border border-slate-200" />
                    <button
                      type="button"
                      className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] hover:bg-red-600"
                      onClick={() => { setCreateImageFile(null); setCreateImagePreview(null); }}
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </div>
                ) : (
                  <div className="w-10 h-10 rounded bg-slate-50 border-2 border-dashed border-slate-200 flex items-center justify-center">
                    <ImagePlus className="w-4 h-4 text-slate-300" />
                  </div>
                )}
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    data-testid="create-item-image-input"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error('File too large. Max 5MB.');
                        return;
                      }
                      setCreateImageFile(file);
                      setCreateImagePreview(URL.createObjectURL(file));
                      e.target.value = '';
                    }}
                  />
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors">
                    <ImagePlus className="w-3.5 h-3.5" />
                    {createImagePreview ? 'Change' : 'Upload'}
                  </span>
                </label>
              </div>
            </div>
          </div>

          <Button data-testid="create-item-btn" onClick={handleCreate} className="w-full bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Create Item
          </Button>
        </CardContent>
      </Card>

      <Card data-testid="item-list-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>All Items ({filteredItems.length} of {items.length})</CardTitle>
              <CardDescription>View and manage all items across plants</CardDescription>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-4 mt-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search by part number or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="search-items"
                />
              </div>
            </div>
            <div className="w-44">
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="FG">FG</SelectItem>
                  <SelectItem value="SFG">SFG</SelectItem>
                  <SelectItem value="BOP">BOP</SelectItem>
                  <SelectItem value="RM-Sheet">RM-Sheet</SelectItem>
                  <SelectItem value="RM-Tube">RM-Tube</SelectItem>
                  <SelectItem value="RM-Bar">RM-Bar</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-44">
              <Select value={customerFilter} onValueChange={setCustomerFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Customers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Customers</SelectItem>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-44">
              <Select value={plantFilter} onValueChange={setPlantFilter}>
                <SelectTrigger data-testid="filter-plant">
                  <SelectValue placeholder="All Plants" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Plants</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {plants.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.code} - {p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {(searchQuery || categoryFilter !== 'all' || customerFilter !== 'all' || plantFilter !== 'all') && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSearchQuery('');
                  setCategoryFilter('all');
                  setCustomerFilter('all');
                  setPlantFilter('all');
                }}
              >
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-auto">
            <table className="w-full caption-bottom text-sm">
              <thead className="[&_tr]:border-b">
                <tr className="border-b transition-colors hover:bg-muted/50">
                  <th className="h-10 px-2 text-left align-middle font-medium w-12">Image</th>
                  <th className="h-10 px-2 text-left align-middle font-medium">Part Number</th>
                  <th className="h-10 px-2 text-left align-middle font-medium">Description</th>
                  <th className="h-10 px-2 text-left align-middle font-medium">Category</th>
                  <th className="h-10 px-2 text-left align-middle font-medium">Plants</th>
                  <th className="h-10 px-2 text-left align-middle font-medium">Customer</th>
                  <th className="h-10 px-2 text-left align-middle font-medium">UOM</th>
                  <th className="h-10 px-2 text-left align-middle font-medium">Cycle Time</th>
                  <th className="h-10 px-2 text-left align-middle font-medium">Safety Days</th>
                  <th className="h-10 px-2 text-left align-middle font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="[&_tr:last-child]:border-0">
                {paginatedItems.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="p-8 text-center text-slate-500">
                      {searchQuery || categoryFilter !== 'all' || customerFilter !== 'all' || plantFilter !== 'all'
                        ? 'No items match your search criteria'
                        : 'No items found'}
                    </td>
                  </tr>
                ) : (
                  paginatedItems.map((item) => (
                  <tr key={item.id} data-testid={`item-row-${item.id}`} className="border-b transition-colors hover:bg-muted/50">
                    <td className="p-2 align-middle">
                      {item.image_url ? (
                        <img
                          src={`${BACKEND_URL}${item.image_url}`}
                          alt={item.part_number}
                          className="w-10 h-10 rounded object-cover border border-slate-200 cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all"
                          data-testid={`item-image-${item.id}`}
                          onClick={() => setImagePreview({ open: true, src: `${BACKEND_URL}${item.image_url}`, alt: `${item.part_number} - ${item.description}` })}
                        />
                      ) : (
                        <div className="w-10 h-10 rounded bg-slate-100 border border-slate-200 flex items-center justify-center">
                          <ImagePlus className="w-4 h-4 text-slate-300" />
                        </div>
                      )}
                    </td>
                    <td className="p-2 align-middle font-medium">{item.part_number}</td>
                    <td className="p-2 align-middle">{item.description}</td>
                    <td className="p-2 align-middle">
                      <Badge className={getCategoryColor(item.category)} title={getCategoryDescription(item.category)}>
                        {item.category}
                      </Badge>
                    </td>
                    <td className="p-2 align-middle">
                      <div className="flex flex-wrap gap-1">
                        {item.plant_ids && item.plant_ids.length > 0 ? (
                          item.plant_ids.map(pid => {
                            const plant = plants.find(p => p.id === pid);
                            return plant ? (
                              <Badge key={pid} variant="outline" className="bg-teal-50 text-teal-700 text-xs whitespace-nowrap">
                                {plant.code}
                              </Badge>
                            ) : null;
                          })
                        ) : (
                          <Badge variant="outline" className="bg-red-50 text-red-600 text-xs">
                            None
                          </Badge>
                        )}
                        <button
                          onClick={() => openPlantAssignDialog(item)}
                          className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-100 hover:bg-blue-100 text-slate-500 hover:text-blue-600 transition-colors ml-0.5"
                          title="Manage plant assignment"
                          data-testid={`assign-plant-${item.id}`}
                        >
                          <Pencil className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    </td>
                    <td className="p-2 align-middle">
                      {item.category === 'FG' ? (
                        item.customer_id ? (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700">
                            {getCustomerName(item.customer_id)}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-red-50 text-red-600">
                            Not Assigned
                          </Badge>
                        )
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="p-2 align-middle">{item.uom}</td>
                    <td className="p-2 align-middle">{item.cycle_time_hrs || 0} hrs</td>
                    <td className="p-2 align-middle">
                      <span className="font-semibold text-blue-600">{item.safety_stock_days || 0}d</span>
                    </td>
                    <td className="p-2 align-middle">
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-blue-600 hover:bg-blue-50"
                          onClick={() => handleEditClick(item)}
                          title="Edit item"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(item)}
                          title="Delete item"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
                )}
              </tbody>
            </table>
          </div>
          
          {filteredItems.length > 0 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <span>Show</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border rounded px-2 py-1"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span>entries</span>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">
                  Showing {Math.min((currentPage - 1) * pageSize + 1, filteredItems.length)} to {Math.min(currentPage * pageSize, filteredItems.length)} of {filteredItems.length}
                </span>
                
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>First</Button>
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="px-3 py-1 text-sm font-medium">Page {currentPage} of {totalPages || 1}</span>
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || totalPages === 0}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages || totalPages === 0}>Last</Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Item Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, item: null })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Item</DialogTitle>
            <DialogDescription>
              Update item details. {editFormData.category === 'FG' && 'Customer is required for FG items.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Part Number *</label>
              <Input
                value={editFormData.part_number || ''}
                onChange={(e) => setEditFormData({ ...editFormData, part_number: e.target.value })}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Description *</label>
              <Input
                value={editFormData.description || ''}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Category</label>
              <Select
                value={editFormData.category || ''}
                onValueChange={(value) => setEditFormData({ ...editFormData, category: value, customer_id: value !== 'FG' ? '' : editFormData.customer_id })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FG">FG - Finished Goods</SelectItem>
                  <SelectItem value="SFG">SFG - Semi-Finished (Make/Buy)</SelectItem>
                  <SelectItem value="BOP">BOP - Bought-Out Parts (Buy Only)</SelectItem>
                  <SelectItem value="RM-Sheet">RM-Sheet - Raw Material Sheet</SelectItem>
                  <SelectItem value="RM-Tube">RM-Tube - Raw Material Tube</SelectItem>
                  <SelectItem value="RM-Bar">RM-Bar - Raw Material Bar</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">UOM</label>
              <Input
                value={editFormData.uom || ''}
                onChange={(e) => setEditFormData({ ...editFormData, uom: e.target.value })}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Cycle Time (hrs)</label>
              <Input
                type="number"
                step="0.1"
                value={editFormData.cycle_time_hrs || 0}
                onChange={(e) => setEditFormData({ ...editFormData, cycle_time_hrs: parseFloat(e.target.value) || 0 })}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Safety Stock Days</label>
              <Input
                type="number"
                min="0"
                value={editFormData.safety_stock_days || 0}
                onChange={(e) => setEditFormData({ ...editFormData, safety_stock_days: parseInt(e.target.value) || 0 })}
              />
            </div>
            
            {editFormData.category === 'FG' && (
              <div className="col-span-2">
                <label className="text-sm font-medium mb-2 block">Customer * (Required for FG)</label>
                <Select
                  value={editFormData.customer_id || ''}
                  onValueChange={(value) => setEditFormData({ ...editFormData, customer_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!editFormData.customer_id && (
                  <p className="text-xs text-red-600 mt-1">Customer must be assigned for FG items</p>
                )}
              </div>
            )}
          </div>

          {/* Image Upload Section */}
          <div className="border border-slate-200 rounded-lg p-4">
            <label className="text-sm font-medium mb-3 block">Part Image</label>
            <div className="flex items-center gap-4">
              {editDialog.item?.image_url ? (
                <div className="relative group">
                  <img
                    src={`${BACKEND_URL}${editDialog.item.image_url}`}
                    alt="Part"
                    className="w-24 h-24 rounded-lg object-cover border border-slate-200 cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all"
                    data-testid="edit-item-image-preview"
                    onClick={() => setImagePreview({ open: true, src: `${BACKEND_URL}${editDialog.item.image_url}`, alt: editFormData.part_number || 'Part Image' })}
                  />
                  <Button
                    size="sm"
                    variant="destructive"
                    className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={handleImageDelete}
                    title="Remove image"
                    data-testid="delete-item-image-btn"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ) : (
                <div className="w-24 h-24 rounded-lg bg-slate-50 border-2 border-dashed border-slate-200 flex items-center justify-center">
                  <ImagePlus className="w-8 h-8 text-slate-300" />
                </div>
              )}
              <div className="flex-1">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="item-image-upload-input"
                  />
                  <div className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                    <ImagePlus className="w-4 h-4" />
                    {uploadingImage ? 'Uploading...' : (editDialog.item?.image_url ? 'Change Image' : 'Upload Image')}
                  </div>
                </label>
                <p className="text-xs text-slate-400 mt-1">JPEG, PNG, WebP, GIF. Max 5MB.</p>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, item: null })}>
              Cancel
            </Button>
            <Button onClick={handleEditSave} className="bg-blue-600 hover:bg-blue-700">
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plant Assignment Dialog */}
      <Dialog open={plantAssignDialog.open} onOpenChange={(open) => !open && setPlantAssignDialog({ open: false, item: null, selectedPlants: [] })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Factory className="w-5 h-5 text-teal-600" />
              Assign to Plants
            </DialogTitle>
            <DialogDescription>
              {plantAssignDialog.item && (
                <span>Select which plants can use <strong>{plantAssignDialog.item.part_number}</strong> - {plantAssignDialog.item.description}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            {plants.map(plant => (
              <label
                key={plant.id}
                className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                data-testid={`assign-checkbox-${plant.id}`}
              >
                <Checkbox
                  checked={plantAssignDialog.selectedPlants.includes(plant.id)}
                  onCheckedChange={() => handlePlantToggle(plant.id)}
                />
                <div className="flex-1">
                  <div className="font-medium text-sm">{plant.name}</div>
                  <div className="text-xs text-slate-500">Code: {plant.code} | {plant.location}</div>
                </div>
                <Badge variant="outline" className="text-xs font-mono">{plant.code}</Badge>
              </label>
            ))}
            {plantAssignDialog.selectedPlants.length === 0 && (
              <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                Warning: Item will not be visible in any plant if no plants are selected.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlantAssignDialog({ open: false, item: null, selectedPlants: [] })}>
              Cancel
            </Button>
            <Button
              data-testid="save-plant-assignment"
              onClick={handleSavePlantAssignment}
              className="bg-teal-600 hover:bg-teal-700"
            >
              <Save className="w-4 h-4 mr-2" />
              Save ({plantAssignDialog.selectedPlants.length} plants)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Preview Lightbox */}
      <Dialog open={imagePreview.open} onOpenChange={(open) => !open && setImagePreview({ open: false, src: '', alt: '' })}>
        <DialogContent className="max-w-3xl p-2 bg-black/95 border-none" data-testid="image-preview-dialog">
          <DialogHeader className="sr-only">
            <DialogTitle>{imagePreview.alt}</DialogTitle>
          </DialogHeader>
          <img
            src={imagePreview.src}
            alt={imagePreview.alt}
            className="w-full h-auto max-h-[80vh] object-contain rounded"
            data-testid="image-preview-full"
          />
          <p className="text-center text-sm text-white/70 mt-1">{imagePreview.alt}</p>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ItemMaster;
